import React, { useState, useEffect, useRef } from 'react';

interface TextInputModalProps {
    show: boolean;
    onHide: () => void;
    onSave: (value: string) => Promise<void>;
    title: string;
    currentValue: string;
    placeholder?: string;
    multiline?: boolean;  // Use textarea instead of input
    maxLength?: number;
}

/**
 * Generic modal for editing text values.
 * Supports single-line input and multi-line textarea.
 */
export const TextInputModal: React.FC<TextInputModalProps> = ({
    show,
    onHide,
    onSave,
    title,
    currentValue,
    placeholder = '',
    multiline = false,
    maxLength
}) => {
    const [value, setValue] = useState(currentValue);
    const [saving, setSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

    // Initialize value when modal opens and focus input
    useEffect(() => {
        if (show) {
            setValue(currentValue);
            // Focus input after modal renders
            setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            }, 100);
        }
    }, [show, currentValue]);

    if (!show) return null;

    const handleSave = async () => {
        if (saving) return;

        const trimmedValue = value.trim();

        // Don't save if value hasn't changed
        if (trimmedValue === currentValue) {
            onHide();
            return;
        }

        setSaving(true);
        try {
            await onSave(trimmedValue);
            onHide();
        } catch (error) {
            console.error('Error saving value:', error);
            alert('Ett fel uppstod vid sparande. Försök igen.');
        } finally {
            setSaving(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !multiline) {
            e.preventDefault();
            handleSave();
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            onHide();
        }
    };

    return (
        <>
            <div
                className="modal show d-block"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                onMouseDown={(e) => {
                    if (e.target === e.currentTarget && !saving) {
                        onHide();
                    }
                }}
            >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{title}</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onHide}
                                aria-label="Stäng"
                                disabled={saving}
                            />
                        </div>
                        <div className="modal-body">
                            {multiline ? (
                                <textarea
                                    ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                                    className="form-control"
                                    value={value}
                                    onChange={(e) => setValue(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={placeholder}
                                    maxLength={maxLength}
                                    disabled={saving}
                                    rows={4}
                                    style={{ resize: 'vertical' }}
                                />
                            ) : (
                                <input
                                    ref={inputRef as React.RefObject<HTMLInputElement>}
                                    type="text"
                                    className="form-control"
                                    value={value}
                                    onChange={(e) => setValue(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={placeholder}
                                    maxLength={maxLength}
                                    disabled={saving}
                                />
                            )}
                            {maxLength && (
                                <small className="text-muted d-block mt-1 text-end">
                                    {value.length} / {maxLength}
                                </small>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onHide}
                                disabled={saving}
                            >
                                Avbryt
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Sparar...
                                    </>
                                ) : (
                                    'Spara'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};
